import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import type { ChatDocument } from 'models/chat';
import MessagePreview from './MessagePreview';
import { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@util/index';
import {
  ChevronDownIcon,
  Loader2Icon,
  MessageCircleDashedIcon,
  MessageCircleOff,
} from 'lucide-react';
import { useConversations } from 'context/ConversationsContext';
import { useAuth } from 'context/AuthContext';
import { ConversationSkeleton } from './ConversationSkeleton';
import { MAIN_FILTERS, type MainFilters } from '@util/firestore/messages';
import { Virtuoso } from 'react-virtuoso';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';

type ConversationListProps = {
  isLoading?: boolean;
  selectedChat: ChatDocument | undefined;
  onChatSelected: (chat: ChatDocument, searchTerm?: string) => void;
  onSearchQueryChange?: (query: string) => void;
  isSidebar?: boolean;
  onCloseSidebar?: () => void;
  isModal?: boolean;
  propChats?: ChatDocument[];
};

export default function ConversationList({
  isLoading,
  selectedChat,
  onChatSelected,
  isSidebar,
  onCloseSidebar,
  isModal,
  propChats,
}: ConversationListProps) {
  const [isOfferDropdownOpen, setIsOfferDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const {
    availableFilters,
    offers,
    searchQuery,
    setSearchQuery,
    activeFilter,
    setActiveFilter,
    debouncedSearchQuery,
    filteredChats,
    isFilteredChatsLoading,
    isFiltered,
    onLoadMore,
    isLoadingMore,
    chats: allChats,
    shouldEnableLoadMore,
    setPreviouslySelectedUnreadChat,
  } = useConversations({});

  const { userDoc } = useAuth();

  const finalChats = useMemo(() => {
    const unfilteredChats = propChats ?? allChats;
    const chats = isFiltered ? filteredChats : unfilteredChats;

    return chats.sort((a, b) => {
      const aIsFavorited = a.favorited?.includes(userDoc?.uid ?? '');
      const bIsFavorited = b.favorited?.includes(userDoc?.uid ?? '');

      // Show favorited chats first
      if (aIsFavorited && !bIsFavorited) return -1;
      if (!aIsFavorited && bIsFavorited) return 1;

      // Keep original order for non-favorited chats
      return b.last_time - a.last_time;
    });
  }, [isFiltered, filteredChats, propChats, allChats, userDoc?.uid]);

  const shouldShowDisableFilterPills = isModal && !!propChats?.length;

  const hasListEnded = !shouldEnableLoadMore && !!finalChats.length;

  const handleChatSelection = useCallback(
    (chat: ChatDocument, searchTerm?: string) => {
      if (activeFilter === 'unread' && userDoc?.uid) {
        if (!selectedChat || selectedChat.id !== chat.id) {
          setPreviouslySelectedUnreadChat(chat.id);
        }
      }

      onChatSelected(chat, searchTerm);
    },
    [
      activeFilter,
      selectedChat,
      userDoc?.uid,
      setPreviouslySelectedUnreadChat,
      onChatSelected,
    ]
  );

  // Calculate dropdown position based on button position
  const calculateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8, // 8px gap below button
        left: rect.left,
      });
    }
  }, []);

  // Handle click outside to close dropdown and search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close dropdown
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOfferDropdownOpen(false);
      }

      // Close expanded search on mobile
      if (
        isSearchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchExpanded(false);
      }
    };

    if (isOfferDropdownOpen || isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOfferDropdownOpen, isSearchExpanded]);

  // Recalculate dropdown position on scroll or resize
  useEffect(() => {
    const handlePositionUpdate = () => {
      if (isOfferDropdownOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOfferDropdownOpen) {
      window.addEventListener('scroll', handlePositionUpdate, true);
      window.addEventListener('resize', handlePositionUpdate);
    }

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate, true);
      window.removeEventListener('resize', handlePositionUpdate);
    };
  }, [isOfferDropdownOpen, calculateDropdownPosition]);

  return (
    <div className="flex h-full w-full flex-col gap-y-4 border-r border-zinc-200 bg-white p-4 lg:w-[55rem]">
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-2 p-4">
          <h3 className="text-[2.5rem] font-semibold text-zinc-900 lg:block">
            Messages
          </h3>

          {/* Mobile Search Icon - Only visible on mobile when search is collapsed */}
          <div className="flex flex-1 justify-end lg:hidden">
            {!isSearchExpanded && (
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                className="flex h-12 w-12 items-center justify-center rounded-full transition-colors hover:bg-zinc-100"
                aria-label="Search messages"
              >
                <MagnifyingGlassIcon className="h-8 w-8 text-zinc-600" />
              </button>
            )}
          </div>
        </div>

        {/* Desktop Search Bar - Always visible on desktop */}
        <div className="hidden h-[6rem] w-full items-center justify-between gap-x-4 rounded-[2rem] border border-zinc-200 bg-zinc-50 pl-4 pr-2 focus-within:border-zinc-400 lg:flex">
          <input
            type="text"
            className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 outline-none placeholder:text-zinc-400"
            placeholder="Search recent messages"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
          />

          <div className="mr-4 flex items-center gap-x-2">
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="group flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-zinc-200"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-10 w-10 text-zinc-400 group-hover:text-zinc-500" />
              </button>
            )}

            <MagnifyingGlassIcon className="h-10 w-10 text-zinc-600" />
          </div>
        </div>

        {/* Mobile Expanded Search Bar - Only visible on mobile when expanded */}
        <AnimatePresence>
          {isSearchExpanded && (
            <motion.div
              ref={searchContainerRef}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden lg:hidden"
            >
              <div className="flex h-[6rem] w-full items-center justify-between gap-x-4 rounded-[2rem] border border-zinc-200 bg-zinc-50 pl-4 pr-2 focus-within:border-zinc-400">
                <input
                  type="text"
                  className="h-full w-full border-none bg-transparent text-[1.8rem] text-zinc-700 outline-none placeholder:text-zinc-400"
                  placeholder="Search recent messages"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  autoFocus
                />

                <div className="mr-4 flex items-center gap-x-2">
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="group flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-zinc-200"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="h-10 w-10 text-zinc-400 group-hover:text-zinc-500" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 flex w-full items-center gap-4 overflow-x-auto overflow-y-visible scroll-smooth py-2">
          {availableFilters.map((filter) => {
            // Special handling for offers filter with dropdown
            if (filter.value === 'offers') {
              const isActive =
                activeFilter === 'offers' || activeFilter.startsWith('offers_');

              return (
                <div
                  key={filter.value}
                  className={cn(
                    'relative flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-4 whitespace-nowrap rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                    isActive
                      ? 'border-black bg-black text-zinc-100'
                      : 'bg-transparent text-zinc-600 hover:bg-zinc-50',
                    shouldShowDisableFilterPills &&
                      'cursor-not-allowed opacity-50',
                    !!propChats &&
                      isActive &&
                      'hover:border-black hover:bg-black'
                  )}
                >
                  <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => {
                      setActiveFilter(filter.value);
                    }}
                    disabled={shouldShowDisableFilterPills}
                  >
                    <span className="flex items-center gap-2">
                      {activeFilter.startsWith('offers_')
                        ? `${
                            MAIN_FILTERS.offer_states[
                              activeFilter as keyof typeof MAIN_FILTERS.offer_states
                            ]
                          } Offers`
                        : filter.label}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (!isOfferDropdownOpen) {
                        calculateDropdownPosition();
                      }

                      setIsOfferDropdownOpen(!isOfferDropdownOpen);
                    }}
                    disabled={shouldShowDisableFilterPills}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'h-[10px] w-[1.3px]',
                          isActive ? 'bg-zinc-400' : 'bg-zinc-300'
                        )}
                      />

                      <ChevronDownIcon
                        className={cn(
                          'pointer-events-none h-7 w-7 transition-transform duration-200',
                          isActive ? 'text-zinc-100' : 'text-zinc-600',
                          isOfferDropdownOpen ? 'rotate-180' : ''
                        )}
                      />
                    </div>
                  </button>
                </div>
              );
            }

            // Regular filter buttons
            const isActive = activeFilter === filter.value;
            return (
              <button
                type="button"
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={cn(
                  'flex h-[4rem] w-fit min-w-[8rem] flex-shrink-0 items-center justify-center gap-2 rounded-full border border-zinc-200 px-6 py-2 text-center font-medium transition-colors',
                  isActive
                    ? 'border-black bg-black text-zinc-100'
                    : 'bg-transparent text-zinc-600 hover:bg-zinc-50',
                  shouldShowDisableFilterPills &&
                    'cursor-not-allowed opacity-50 hover:bg-transparent',
                  !!propChats && isActive && 'hover:border-black hover:bg-black'
                )}
                disabled={shouldShowDisableFilterPills}
              >
                <div className="flex items-center gap-2">{filter.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      {(isLoading || isFilteredChatsLoading) &&
      (!finalChats.length || isFiltered) ? (
        <div className="flex h-full flex-col gap-6">
          {Array.from({ length: 11 }, (_, index) => index).map(
            (skeletonIndex) => (
              <ConversationSkeleton key={`skeleton-${skeletonIndex}`} />
            )
          )}
        </div>
      ) : finalChats.length === 0 && debouncedSearchQuery.trim() ? (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <MagnifyingGlassIcon className="h-16 w-16 text-zinc-300" />
            <span className="max-w-[40rem] text-center text-[1.6rem] text-zinc-400">
              No messages found for &quot;{debouncedSearchQuery.trim()}&quot;
            </span>
          </div>
        </div>
      ) : finalChats.length === 0 && !debouncedSearchQuery.trim() ? (
        <div className="flex h-full items-center justify-center">
          <div className="flex max-w-sm flex-col items-center gap-6 text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-zinc-100">
              <MessageCircleDashedIcon className="h-12 w-12 text-zinc-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-[1.8rem] font-medium text-zinc-700">
                No messages found
              </h3>
            </div>
          </div>
        </div>
      ) : (
        <div className="h-full">
          <Virtuoso
            style={{ height: '100%' }}
            data={finalChats}
            itemContent={(_, chat) => (
              <MessagePreview
                key={chat.id + chat.last_time}
                chat={chat}
                onClick={() => {
                  handleChatSelection(
                    chat,
                    debouncedSearchQuery.trim() || undefined
                  );
                }}
                selected={selectedChat?.id === chat.id}
                isSidebar={isSidebar}
                chats={finalChats}
                searchQuery={debouncedSearchQuery}
                activeFilter={activeFilter}
              />
            )}
            endReached={shouldEnableLoadMore ? onLoadMore : undefined}
            components={{
              Footer: () =>
                hasListEnded && !isLoading && !isLoadingMore ? (
                  <div className="flex w-full justify-center py-4">
                    <div className="flex w-fit items-center gap-4 rounded-[2rem] border-2 border-dashed border-zinc-200 bg-white/60 px-8 py-4 backdrop-blur-3xl">
                      <MessageCircleOff className="h-6 w-6 text-zinc-500" />

                      <span className="text-[1.5rem] text-zinc-500">
                        You&apos;ve reached the end of the list.
                      </span>
                    </div>
                  </div>
                ) : null,
            }}
          />

          <AnimatePresence>
            {isLoadingMore && (
              <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                  duration: 0.4,
                }}
                className="relative flex w-full justify-center"
              >
                <div className="absolute bottom-4 flex items-center gap-4 rounded-[2rem] border border-zinc-200 bg-white/60 px-8 py-4 backdrop-blur-3xl">
                  <Loader2Icon className="h-6 w-6 animate-spin text-zinc-500" />
                  <span className="text-[1.5rem] text-zinc-500">
                    Loading more chats...
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Dropdown Menu - Fixed positioned to escape scroll container */}
      {isOfferDropdownOpen && (
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-48 rounded-2xl border border-zinc-200 bg-white shadow-lg"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="py-2">
            {Object.keys(MAIN_FILTERS.offer_states).map((state) => (
              <button
                key={state}
                type="button"
                onClick={() => {
                  setActiveFilter(state as MainFilters);
                  setIsOfferDropdownOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-3 text-left text-[1.4rem] transition-colors hover:bg-zinc-50',
                  activeFilter === state
                    ? 'bg-zinc-100 font-medium text-zinc-900'
                    : 'text-zinc-600',
                  shouldShowDisableFilterPills &&
                    'cursor-not-allowed opacity-50 hover:bg-transparent'
                )}
                disabled={shouldShowDisableFilterPills}
              >
                {
                  MAIN_FILTERS.offer_states[
                    state as keyof typeof MAIN_FILTERS.offer_states
                  ]
                }
              </button>
            ))}
          </div>
        </div>
      )}

      {/* View All Messages Button - Only visible in sidebar mode */}
      {isSidebar && (
        <>
          <div className="absolute bottom-0 z-20 w-full">
            <div className="flex w-full flex-col gap-6 py-12 pr-8">
              <Link
                href="/dashboard/messages"
                className="flex h-[6rem] w-full items-center justify-center rounded-[2rem] bg-brand-secondary text-center text-[2.1rem] font-semibold text-white transition-colors duration-75 ease-linear hover:bg-brand-secondary-darker"
                onClick={() => {
                  onCloseSidebar?.();
                }}
              >
                View all messages
              </Link>
            </div>
          </div>

          <div className="absolute bottom-0 z-10 h-[30rem] w-full bg-gradient-to-t from-white to-transparent" />
        </>
      )}
    </div>
  );
}
